"use client";

import { Check, Copy, ExternalLink, Upload } from "lucide-react";
import { useRef, useState } from "react";
import Link from "@/components/locale-link";
import { useToolLocale, useToolUi } from "@/components/tools/tool-ui";
import { writeToClipboard } from "@/lib/clipboard";
import { extractPdfDois } from "@/lib/document-extract";
import { t, tPlural } from "@/lib/i18n/interpolate";

type ScanState =
  | { status: "idle" }
  | { status: "busy"; fileName: string }
  | { status: "error"; message: string }
  | { status: "done"; fileName: string; dois: string[] };

/** English strings. A translated content file overrides these through `ui`. */
export const PDF_CITATION_GENERATOR_STRINGS = {
  scannedLocallyNothingIsUploaded: "Scanned locally — nothing is uploaded",
  howItWorks: "How it works",
  publishersEmbedTheArticleS: "Publishers embed the article’s ",
  doi: "DOI",
  inThePdfSMetadata:
    " in the PDF’s metadata and page content. The tool scans the raw file and inflates its compressed streams — entirely",
  inYourBrowser: "in your browser",
  toFindEveryDoi: " — to find every DOI.",
  citingByDoiBeatsScraping:
    "Citing by DOI beats scraping the PDF’s text: the generator pulls the publisher’s official metadata from Crossref, so authors, title, journal, volume, and pages are exact.",
  workingWithAWordDocument: "Working with a Word document instead? The",
  referenceExtractor: "Reference Extractor",
  pullsAFullReferenceList: "pulls a full reference list out of a .docx.",
  clickToUploadAPdf: "Click to upload a PDF",
  scanningFilename: "Scanning {fileName}…",
  couldNotReadTheFile: "Could not read the file.",
  results: "Results",
  copied: "Copied",
  copyFailed: "Copy failed",
  copy: "Copy",
  copyAllDois: "Copy all DOIs",
  cite: "Cite",
  noDoiWasFoundIn: "No DOI was found in ",
  thisUsuallyMeansThePdf:
    ". This usually means the PDF is a scan, an older paper, or not a journal article.",
  nextBestOptionsSearchTheArticles: "Next best options: search the article’s",
  titleInTheCitationGenerator: "title in the citation generator",
  itLooksUp100mWorksOrCite:
    "(it looks up 100M+ works), or cite it manually with the source form.",
  doisFound: "one: {count} DOI found|other: {count} DOIs found",
  inFilename: "in {fileName}",
  firstDoiIsAlmostAlways:
    "The first DOI is almost always the article itself; later ones are usually its references. Click",
  toOpenTheGeneratorWithDoi:
    "to open the generator with the DOI prefilled — it fetches the official metadata and formats the citation in your chosen style.",
  likelyThisPapersOwnDoi: "Likely this paper’s own DOI",
} as const;

type Strings = typeof PDF_CITATION_GENERATOR_STRINGS;

function CopyButton({
  text,
  label,
  copiedLabel,
  failedLabel,
}: {
  text: string;
  label: string;
  copiedLabel: string;
  failedLabel: string;
}) {
  const [copied, setCopied] = useState(false);
  const [failed, setFailed] = useState(false);

  async function handleCopy() {
    const ok = await writeToClipboard(text);
    if (ok) {
      setCopied(true);
      setFailed(false);
      setTimeout(() => setCopied(false), 1500);
    } else {
      setFailed(true);
    }
  }

  function buttonLabel(): string {
    if (copied) {
      return copiedLabel;
    }
    if (failed) {
      return failedLabel;
    }
    return label;
  }

  return (
    <button
      className="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-2.5 py-1 font-medium text-foreground text-xs hover:bg-muted"
      onClick={handleCopy}
      type="button"
    >
      {copied ? (
        <Check aria-hidden="true" className="size-3.5" />
      ) : (
        <Copy aria-hidden="true" className="size-3.5" />
      )}
      {buttonLabel()}
    </button>
  );
}

function DoiResults({
  fileName,
  dois,
  strings,
  locale,
}: {
  fileName: string;
  dois: string[];
  strings: Strings;
  locale: string;
}) {
  if (dois.length === 0) {
    return (
      <section aria-live="polite" className="space-y-3">
        <h2 className="font-semibold text-foreground text-xl">
          {strings.results}
        </h2>
        <div className="rounded-lg border border-border bg-card p-5">
          <p className="text-foreground text-sm leading-relaxed">
            {strings.noDoiWasFoundIn}
            <strong>{fileName}</strong>
            {strings.thisUsuallyMeansThePdf}
          </p>
          <p className="mt-3 text-muted-foreground text-sm leading-relaxed">
            {strings.nextBestOptionsSearchTheArticles}{" "}
            <Link
              className="text-primary underline-offset-4 hover:underline"
              href="/"
            >
              {strings.titleInTheCitationGenerator}
            </Link>{" "}
            {strings.itLooksUp100mWorksOrCite}
          </p>
        </div>
      </section>
    );
  }

  return (
    <section aria-live="polite" className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="font-semibold text-foreground text-xl">
          {tPlural(strings.doisFound, locale, dois.length)}{" "}
          <span className="font-normal text-muted-foreground text-sm">
            {t(strings.inFilename, { fileName })}
          </span>
        </h2>
        {dois.length > 1 ? (
          <CopyButton
            copiedLabel={strings.copied}
            failedLabel={strings.copyFailed}
            label={strings.copyAllDois}
            text={dois.join("\n")}
          />
        ) : null}
      </div>
      <p className="text-muted-foreground text-sm leading-relaxed">
        {strings.firstDoiIsAlmostAlways} <strong>{strings.cite}</strong>{" "}
        {strings.toOpenTheGeneratorWithDoi}
      </p>
      <ul className="space-y-2">
        {dois.map((doi, index) => (
          <li
            className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border bg-card p-4"
            key={doi}
          >
            <div className="min-w-0">
              <p className="break-all font-mono text-foreground text-sm">
                {doi}
              </p>
              {index === 0 ? (
                <p className="mt-1 text-muted-foreground text-xs">
                  {strings.likelyThisPapersOwnDoi}
                </p>
              ) : null}
            </div>
            <div className="flex shrink-0 gap-2">
              <CopyButton
                copiedLabel={strings.copied}
                failedLabel={strings.copyFailed}
                label={strings.copy}
                text={doi}
              />
              <a
                className="inline-flex items-center gap-1.5 rounded-md border border-primary bg-primary px-2.5 py-1 font-medium text-primary-foreground text-xs hover:bg-primary/90"
                href={`/?source=${encodeURIComponent(doi)}`}
              >
                <ExternalLink aria-hidden="true" className="size-3.5" />
                {strings.cite}
              </a>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

export default function PdfCitationGeneratorWidget() {
  const [state, setState] = useState<ScanState>({ status: "idle" });
  const fileInputRef = useRef<HTMLInputElement>(null);
  const locale = useToolLocale();
  const strings = useToolUi(PDF_CITATION_GENERATOR_STRINGS);

  async function handleFile(file: File) {
    setState({ status: "busy", fileName: file.name });
    try {
      const dois = await extractPdfDois(await file.arrayBuffer());
      setState({ status: "done", fileName: file.name, dois });
    } catch (error) {
      const message =
        error instanceof Error ? error.message : strings.couldNotReadTheFile;
      setState({ status: "error", message });
    }
  }

  return (
    <>
      {/* Upload */}
      <div className="space-y-3">
        <button
          className="flex w-full flex-col items-center gap-3 rounded-lg border-2 border-border border-dashed bg-card p-10 text-center transition-colors hover:border-primary hover:bg-muted/40"
          disabled={state.status === "busy"}
          onClick={() => fileInputRef.current?.click()}
          type="button"
        >
          <Upload aria-hidden="true" className="size-8 text-muted-foreground" />
          <span className="font-medium text-foreground text-sm">
            {state.status === "busy"
              ? t(strings.scanningFilename, { fileName: state.fileName })
              : strings.clickToUploadAPdf}
          </span>
          <span className="text-muted-foreground text-xs">
            {strings.scannedLocallyNothingIsUploaded}
          </span>
        </button>
        <input
          accept=".pdf,application/pdf"
          className="hidden"
          onChange={(event) => {
            const file = event.target.files?.[0];
            if (file) {
              handleFile(file);
            }
            event.target.value = "";
          }}
          ref={fileInputRef}
          type="file"
        />
        {state.status === "error" ? (
          <p className="rounded-lg border border-destructive/40 bg-destructive/10 p-3 text-destructive text-sm">
            {state.message}
          </p>
        ) : null}
      </div>
      {/* Results */}
      {state.status === "done" ? (
        <DoiResults
          dois={state.dois}
          fileName={state.fileName}
          locale={locale}
          strings={strings}
        />
      ) : null}
      {/* How it works */}
      <section className="space-y-4 rounded-lg border border-border bg-muted/30 p-6">
        <h2 className="font-semibold text-foreground text-lg">
          {strings.howItWorks}
        </h2>
        <ul className="list-disc space-y-2 pl-5 text-muted-foreground text-sm leading-relaxed">
          <li>
            {strings.publishersEmbedTheArticleS}
            <strong>{strings.doi}</strong>
            {strings.inThePdfSMetadata} <strong>{strings.inYourBrowser}</strong>
            {strings.toFindEveryDoi}
          </li>
          <li>{strings.citingByDoiBeatsScraping}</li>
          <li>
            {strings.workingWithAWordDocument}{" "}
            <Link
              className="text-primary underline-offset-4 hover:underline"
              href="/tools/reference-extractor"
            >
              {strings.referenceExtractor}
            </Link>{" "}
            {strings.pullsAFullReferenceList}
          </li>
        </ul>
      </section>
    </>
  );
}
